/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  IconButton,
  DialogActions,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import type { TransitionProps } from '@mui/material/transitions';
import { useAddToCartMutation } from 'renderer/features/payments-page/services/cart.endpoints';

interface PurchaseDialogProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  productId: string | null;
  products: any[];
  platform: string;
  hostApp: any;
  quantity?: number;
  redirectToPurchasePage?: boolean;
  fromAddToCart?: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PurchaseDialog: React.FC<PurchaseDialogProps> = (props) => {
  const {
    open,
    onClose,
    onNavigate,
    productId,
    products,
    platform,
    hostApp,
    redirectToPurchasePage,
    quantity,
    fromAddToCart,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [addToCart] = useAddToCartMutation();

  // ✅ normalize instead of returning early
  const safeProducts = Array.isArray(products) ? products : [];

  // const { product } =
  //   (() => {
  //     const selectedProduct =
  //       safeProducts.find((p) => p._id === productId) || null;
  //     const subscriptions = safeProducts.filter(
  //       (p) => p.category === 'Subscription',
  //     );
  //     const selectedSingleHostApp =
  //       subscriptions.find(
  //         (p) => p.name?.toLowerCase().includes('single host'),
  //       ) || null;
  //     const selectedAllAccess =
  //       subscriptions.find(
  //         (p) => p.name?.toLowerCase().includes('all access'),
  //       ) || null;

  //     const [allAccess2] = subscriptions.filter(
  //       (p) => p.name.toLowerCase().indexOf('all access') >= 0,
  //     );
  //     return {
  //       product: selectedProduct,
  //       singleHostApp: selectedSingleHostApp,
  //       allAccess: allAccess2,
  //     };
  //   },
  //   [safeProducts, productId]);

  // ---- Helpers ----
  const handleAdd = useCallback(
    async (
      payload: {
        productId: string | null;
        quantity?: number;
        platform?: string;
        hostApplication?: string;
      },
      navigateTo: string,
    ) => {
      if (!payload.productId) return;
      await addToCart({
        productId: payload.productId,
        quantity: payload.quantity ?? 1,
        platform: payload.platform,
        hostApplication: payload.hostApplication,
      });
      onNavigate(navigateTo);
    },
    [addToCart, onNavigate],
  );
  if (!products) return null;

  const [product] = products.filter((p) => p._id === productId);
  const subscriptions = products.filter((p) => p.category === 'Subscription');

  const [singleHostApp] = subscriptions.filter(
    (p) => p.name.toLowerCase().indexOf('single host') >= 0,
  );

  const [allAccess] = subscriptions.filter(
    (p) => p.name.toLowerCase().indexOf('all access') >= 0,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="purchase-dialog-title"
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      keepMounted
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          // On mobile, ensure the dialog honors the visual viewport (address bar collapse):
          maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      {/* Close button (bigger touch target on mobile) */}
      <IconButton
        onClick={onClose}
        aria-label="Close purchase dialog"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          p: 1.25,
          zIndex: 2,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          backgroundColor: '#f9f9f9',
          // Tighter padding on mobile, comfy on desktop:
          py: { xs: 2, sm: 5 },
          px: { xs: 2, sm: 4 },
          // Allow internal scrolling while keeping footer sticky:
          overflowY: 'auto',
        }}
      >
        <Typography
          id="purchase-dialog-title"
          variant={isMobile ? 'h6' : 'h5'}
          fontWeight={700}
          textAlign="center"
          mb={{ xs: 1.5, sm: 2 }}
        >
          Choose Your Purchase
        </Typography>

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ opacity: 0.8, mb: { xs: 2, sm: 3 } }}
        >
          Compare options and continue.
          {/* {singleHostApp && <>singleHostApp:{singleHostApp._id}</>} */}
          <br />
          {/* {allAccess && <>allAccess:{allAccess._id}</>} */}
        </Typography>

        <Grid
          container
          spacing={{ xs: 2, sm: 4 }}
          // Stack cards on mobile, side-by-side on desktop:
          alignItems="stretch"
        >
          {/* Subscription Card */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                border: '1px solid #c8e6c9',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: '#66bb6a',
                    color: 'white',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    display: 'inline-block',
                    mb: 1,
                  }}
                >
                  Most Popular
                </Typography>

                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  align="center"
                  fontWeight={700}
                  mb={1.5}
                  color="primary"
                >
                  Subscription
                </Typography>

                <List
                  dense
                  sx={{
                    '& .MuiListItemIcon-root': { minWidth: 32 },
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {[
                    'Access to all our plugins for 12 months.',
                    'New plugins included at no extra cost.',
                    'Unlimited phone & email support.',
                    'Includes updates and upgrades.',
                  ].map((text, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ sx: { color: 'black' } }}
                        primary={text}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box mt="auto" display="flex" flexDirection="column" gap={1.25}>
                {singleHostApp && (
                  <Button
                    fullWidth
                    variant="contained"
                    size={isMobile ? 'large' : 'medium'}
                    sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                    onClick={() =>
                      handleAdd(
                        {
                          productId: singleHostApp._id,
                          platform:
                            singleHostApp.supportedPlatforms?.[0] ??
                            'All Platforms',
                        },
                        `/cart/${singleHostApp._id}`,
                      )
                    }
                  >
                    Buy One Host App
                  </Button>
                )}
                {allAccess && (
                  <Button
                    fullWidth
                    variant="contained"
                    size={isMobile ? 'large' : 'medium'}
                    sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                    onClick={() =>
                      handleAdd(
                        {
                          productId: allAccess._id,
                          platform:
                            allAccess.supportedPlatforms?.[0] ??
                            'All Platforms',
                          hostApplication:
                            allAccess.supportedHostApplications?.[0],
                        },
                        `/cart/${allAccess._id}`,
                      )
                    }
                  >
                    Buy All Access
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Perpetual License Card */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                backgroundColor: '#fafafa',
                color: '#333',
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                border: '1px solid #ddd',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  align="center"
                  fontWeight={700}
                  mb={1.5}
                  mt={{ xs: 1, sm: 2 }}
                >
                  Perpetual License
                </Typography>

                <List
                  dense
                  sx={{
                    '& .MuiListItemIcon-root': { minWidth: 32 },
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {[
                    'One year of free updates and upgrades.',
                    'Plugin-only: includes a single plugin.',
                    'Phone & email support for one year.',
                    'Upgrade fees may apply later.',
                  ].map((text, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box mt="auto">
                <Button
                  fullWidth
                  variant="contained"
                  size={isMobile ? 'large' : 'medium'}
                  sx={{ fontWeight: 'bold', py: 1.1, borderRadius: 2 }}
                  onClick={() =>
                    handleAdd(
                      {
                        productId: productId ?? product?._id ?? null,
                        quantity: quantity || 1,
                        platform:
                          platform ||
                          product?.supportedPlatforms?.[0] ||
                          'All Platforms',
                        hostApplication: hostApp,
                      },
                      redirectToPurchasePage
                        ? `/purchase`
                        : `/cart/${productId}`,
                    )
                  }
                >
                  {fromAddToCart ? 'Add Plugin Only' : 'Buy Plugin'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Light divider to separate from sticky footer on mobile */}
        {isMobile && <Divider sx={{ mt: 2 }} />}
      </DialogContent>

      {/* Sticky on mobile, normal on desktop */}
      <DialogActions
        sx={{
          position: { xs: 'sticky', sm: 'static' },
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: { xs: '1px solid', sm: 'none' },
          borderColor: { xs: 'divider', sm: 'transparent' },
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          // On mobile, stack actions vertically if more than one (we have only Close here, but future-proof):
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={onClose}
          color="primary"
          // variant={isMobile ? 'contained' : 'text'}
          variant="text"
          // fullWidth={isMobile}
          size={isMobile ? 'large' : 'medium'}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PurchaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  productId: PropTypes.string,
};

export default PurchaseDialog;
